import axios from "axios";
import {
  CommentType,
  DictionaryType,
  LessonType,
  EventType,
  ProgressType,
  SentenceType,
  StoryType,
  TagType,
  TranscriptType,
  WordGroupType,
  CourseType,
  VerbFormType,
  MediaType,
  UserType,
  VocabularyType,
  FeedbackType,
  UserProp,
  PromoType,
  PremiumType,
  PersonType,
} from "App.types";
import { instance } from "App";
import { isNumber } from "lodash";

const API = {
  ai: {
    getLesson: (id: number) => instance.get<LessonType | undefined>(`/ai/lesson/${id}`).then(({ data }) => data),
  },
  person: { getList: () => instance.get<PersonType[]>("/person").then(({ data }) => data) },
  chat: {
    answer: (data: { storyId: number; message: string }) =>
      instance
        .post<{ text: string; isCompleted: boolean; isWrong: boolean; comments: string }>("chat/answer", data)
        .then(({ data }) => data),
    send: (data: { storyId: number; message: string }) =>
      instance.post<{ text: string; isCompleted: boolean; isWrong: boolean; comments: string }>("chat/send", data).then(({ data }) => data),
    messages: (storyId: number) =>
      instance.get<{ role: string; text: string; isCompleted: boolean }[]>(`chat/messages/${storyId}`).then(({ data }) => data),
    answers: (storyId: number) =>
      instance.get<{ role: string; text: string; isCompleted: boolean }[]>(`chat/answers/${storyId}`).then(({ data }) => data),
    delete: (storyId: number) => instance.delete(`chat/${storyId}`).then(({ data }) => data),
  },
  premium: {
    cancel: () => instance.post("premium/cancel").then(({ data }) => data),
    return: () => instance.post("premium/return").then(({ data }) => data),
    get: () => instance.get<PremiumType>("premium").then(({ data }) => data),
    subscription: () => instance.get<any>("premium/subscription/").then(({ data }) => data),
    activate: (data: any) => instance.post<any>("premium/activate/", data).then(({ data }) => data),
  },
  promo: {
    findCode: (code: string) => instance.get<PromoType>("/promo", { params: { code } }).then(({ data }) => data),
  },
  feedback: {
    save: (feedback: Partial<FeedbackType>) => instance.post("/feedback", feedback).then(({ data }) => data),
    getReports: (params: Partial<FeedbackType>) => instance.get<FeedbackType[]>("/feedback/reports", { params }).then(({ data }) => data),
  },
  media: {
    getById: (id: string | number) => instance.get<MediaType>(`/media/${id}`).then(({ data }) => data),
  },
  lang: { get: () => instance.get<"en" | "de">("").then(({ data }) => data) },
  user: {
    delete: () => instance.delete("/users").then(({ data }) => data),
    updateProp: (name: string, value: string | number) => instance.patch("/users/props", { name, value }).then(({ data }) => data),
    getProps: () => instance.get<UserProp[]>("/users/props").then(({ data }) => data),
    update: (user: Partial<UserType>) => instance.post("/users", user).then(({ data }) => data),
    get: () => instance.get<{ level: number }>("users").then(({ data }) => data),
    yandexCode: (code: string) => instance.get<{ level: number }>("users/yandex", { params: { code } }).then(({ data }) => data),
    getSpeechToken: () => instance.get<{ token: string; region: string }>("users/get-speech-token").then(({ data }) => data),
    getRecToken: () => instance.get<{ token: string }>("users/get-rec-token").then(({ data }) => data),
    getJwtToken: () => instance.get<{ token: string }>("users/jwt-speech").then(({ data }) => data),
    getDGToken: () =>
      instance
        .get<{ key: string; api_key_id: string; comment: string; expiration_date: string; created: string }>("users/get-dg-token")
        .then(({ data }) => data),
  },
  story: {
    getList: (type: string = "story") => instance.get<StoryType[]>(`stories/type/${type}`).then(({ data }) => data),
    getByTitle: (title: string) => instance.get<StoryType>("stories/title", { params: { title } }).then(({ data }) => data),
    get: (id: string | number) => instance.get<StoryType>(`stories/${id}`).then(({ data }) => data),
    getById: (id: string | number) => instance.get<StoryType>("stories", { params: { id } }).then(({ data }) => data),
  },
  sentence: {
    hint: (id: string | number, answer: string, storyId: number, description: string) =>
      instance.post<{ hint: string }>(`sentences/hint/${id}`, { answer, description, storyId }).then(({ data }) => data),
    check: (id: string | number, body: { answer: string; lessonId: number; storyId?: number; taskDescription?: string }) =>
      instance.post<{ wrong: boolean; comments: string; answer: string }>(`sentences/check/${id}`, body).then(({ data }) => data),
    getIntros: (course: string) => instance.get<SentenceType[]>("sentences/intros", { params: { course } }).then(({ data }) => data),
    getById: (sentenceId: string | number) => instance.get(`sentences/get/${sentenceId}`).then(({ data }) => data),
    getListByStoryId: (storyId: string | number) => instance.get<SentenceType[]>(`sentences/${storyId}`).then(({ data }) => data),
  },
  dictionary: {
    findOne: (word: string, pos?: string) =>
      instance.get<DictionaryType>(`/dictionary/findOne/${word}`, { params: { pos } }).then(({ data }) => data),
    find: (word: string, pos?: string) =>
      instance.get<DictionaryType[]>(`dictionary/find/${word}`, { params: { pos } }).then(({ data }) => data),
    full: (word: string, pos?: string, tag?: string) =>
      instance.get<DictionaryType>(`dictionary/full/${word}`, { params: { pos, tag } }).then(({ data }) => data),
    audio: (word: string) => instance.get(`dictionary/audio/${word}`, { responseType: "blob" }).then((resp) => resp.data),
  },
  progress: {
    getActivity: () => instance.get<{ day: string; count: string }[]>("progress/activity").then(({ data }) => data),
    delete: (id: string | number) => instance.delete(`progress/${id}`).then(({ data }) => data),
    deleteTask: (params: { task: string | undefined; lessonId: number; sentenceId: number }) =>
      instance.delete("progress/task", { params }).then(({ data }) => data),
    getVocabularyCount: () => instance.get<number>("progress/vocabulary/count").then(({ data }) => data),
    getVocabulary: () => instance.get<VocabularyType[]>("progress/vocabulary").then(({ data }) => data),
    save: (progress: Partial<ProgressType>) => instance.post("progress", progress).then(({ data }) => data),
    saveVocabularyProgress: (words: { word: string; pos: string }[]) =>
      instance.post("progress/vocabulary", words).then(({ data }) => data),
    getForCourse: (course: string | undefined) =>
      instance.get<ProgressType[]>("progress/course", { params: { course } }).then(({ data }) => data),
    getForLesson: (lesson: number) => instance.get<ProgressType[]>("progress/lesson", { params: { lesson } }).then(({ data }) => data),
    get: (progress: Partial<ProgressType>) => instance.get<ProgressType>("progress", { params: progress }).then(({ data }) => data),
  },
  event: {
    save: (event: Partial<EventType>) => instance.post("users/event", event).then(({ data }) => data),
  },
  transcriptions: {
    getBySentId: (sentId: number) => instance.get<TranscriptType[]>("audio/transcript", { params: { sentId } }).then(({ data }) => data),
    getByStoryId: (storyId: number) => instance.get<TranscriptType[]>(`audio/transcript/${storyId}`).then(({ data }) => data),
  },
  conversation: {
    getMessages: (topicId: number) =>
      instance
        .get<{ role: string; text: string; content: string }[]>("conversation/messages", { params: { topicId } })
        .then(({ data }) => data),
    start: (storyId: number, voice?: string) =>
      instance.get("conversation/start", { params: { storyId, voice }, responseType: "arraybuffer" }).then(({ data }) => data),
    deleteTopic: (id: number) => instance.delete<{}>(`conversation/topic/${id}`).then(({ data }) => data),
    getTopic: (storyId: number, type?: string) =>
      instance.get<{ id: number; title: string }>("conversation/topic", { params: { title: storyId, type } }).then(({ data }) => data),
    send: (data: { storyId: number; message: string; teacher: boolean }) =>
      instance
        .post<{ answer: { text: string; translate: string }[]; result: string; messageId?: number }>("conversation/send", data)
        .then(({ data }) => data),
  },
  practice: {
    check: (body: { storyId: string | number; originalStoryId?: string | number; text: string }) =>
      instance.post<string>("practice/check", body).then(({ data }) => data),
    speech: (form: FormData) =>
      instance.post<{ transcript: string; wrong: boolean; comments: string }>("practice/speech", form).then(({ data }) => data),
  },
  speech: {
    getSpeechWords: (words: string[]) =>
      instance.get<{ word: string; transcript: string }[]>("speech/words", { params: { words } }).then(({ data }) => data),
    getForSentence: (params: { sentId: number; text?: string; voice: string; style?: string }) =>
      instance.get("speech/sent", { params, responseType: "arraybuffer" }).then(({ data }) => data),
    getJson: (sentenceId: number, voice: string, voiceStyle?: string) =>
      instance
        .get<{ duration: number; start: number; end: number; text: string }[]>("speech/json", {
          params: { sentenceId, voice, style: voiceStyle },
        })
        .then(({ data }) => data),
  },
  grammar: {
    spellCheck: (text: string) =>
      instance.get<{ word: string; s: string[] }[]>("grammar/spellcheck", { params: { text } }).then(({ data }) => data),
    getList: () => instance.get("grammar").then(({ data }) => data),
    get: (id: string) => instance.get(`grammar/${id}`).then(({ data }) => data),
    getTokens: (text: string) => instance.get("grammar/tokens", { params: { text } }).then(({ data }) => data),
  },
  wordGroup: {
    find: (word: string) => instance.get<WordGroupType>(`word-group/${word}`).then(({ data }) => data),
    gelList: (ids?: number[]) =>
      instance
        .get<WordGroupType[]>("word-group", { params: { ids: ids?.filter((el) => isNumber(el)).join(",") } })
        .then(({ data }) => data),
    getForms: (verbs: string[]) =>
      instance.get<VerbFormType[]>("word-group/verb-forms", { params: { verbs: verbs.join(",") } }).then(({ data }) => data),
  },
  course: {
    getAll: () => instance.get<[CourseType & { lessons: number[] }]>("/course/all").then(({ data }) => data),
    getList: () => instance.get<Array<CourseType & { lessons: LessonType[] }>>("/course/").then(({ data }) => data),
  },
  lesson: {
    getList: (course?: string) => instance.get<LessonType[]>("lesson", { params: { course } }).then(({ data }) => data),
    get: (id: string | number, page: string | number | null = "") =>
      instance.get<LessonType & { course: CourseType }>(`lesson/${id}`, { params: { page } }).then(({ data }) => data),
  },
  comment: {
    post: (comment: Partial<CommentType>) => instance.post("users/comment", comment).then(({ data }) => data),
    getList: (sentence: Partial<SentenceType>) =>
      instance.get<CommentType[]>("users/comments", { params: { sentence } }).then(({ data }) => data),
    getCount: (id: string | number) =>
      instance.get<number>("users/comments/count", { params: { sentence: { id } } }).then(({ data }) => data),
  },
  lemma: {
    get: (text: string) => axios.get<TagType[]>("https://api.lingobro.com/tagger/", { params: { text } }).then(({ data }) => data),
  },
  speller: {
    get: (nodes: { text: string }[]) =>
      axios
        .get<{ code: number; pos: number; word: string; s: string[] }[][]>(
          `https://speller.yandex.net/services/spellservice.json/checkTexts?text=${nodes.map((el) => el.text).join("&text=")}`,
        )
        .then(({ data }) => data),
    checkText: (text: string) =>
      axios
        .get<{ code: number; pos: number; word: string; s: string[] }[]>(
          `https://speller.yandex.net/services/spellservice.json/checkText?text=${text}`,
        )
        .then(({ data }) => data),
  },
};

export default API;
